import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Upload, User, Plus, Trash2 } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const SEXO_OPTIONS = [{ value: "M", label: "Masculino" }, { value: "F", label: "Femenino" }];
const BLOOD_TYPES = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];

export default function AlumnoSeccionDetalle() {
    const { section: sectionParam, studentId } = useParams();
    const section = sectionParam || "medico"; // ruta de psicólogo no lleva :section, siempre es médico
    const navigate = useNavigate();
    const { user } = useAuth();
    const canEdit = user?.role === "administrativo" || user?.role === "ejecutivo";

    const [payload, setPayload] = useState(null);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/student-details/${studentId}/${section}`);
            setPayload(data);
            setForm(data.data || {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando la información");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [section, studentId]);

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/student-details/${studentId}/${section}`, form);
            toast.success("Guardado correctamente");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando");
        } finally {
            setSaving(false);
        }
    }

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!payload) return <div className="p-10 text-edu-muted">No se pudo cargar.</div>;

    const titles = { datos: "Datos del alumno", familia: "Familia", medico: "Datos médicos-psicológicos" };

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="edu-card p-6 mb-6 flex items-center gap-4">
                {payload.student_photo_id ? (
                    <img src={fileUrl(payload.student_photo_id)} alt={payload.student_name} className="w-16 h-16 rounded-full object-cover border border-edu" />
                ) : (
                    <div className="w-16 h-16 rounded-full bg-edu-soft flex items-center justify-center border border-edu">
                        <User size={24} className="text-edu-muted" />
                    </div>
                )}
                <div>
                    <p className="text-xs text-edu-muted uppercase tracking-widest font-semibold">{titles[section]}</p>
                    <h1 className="text-xl font-semibold">{payload.student_name}</h1>
                    <p className="text-xs text-edu-muted">{payload.student_grade}</p>
                </div>
            </div>

            {!canEdit && (
                <p className="text-xs text-edu-muted bg-edu-soft rounded-xl p-3 mb-4">Solo lectura — la edición es exclusiva del personal administrativo.</p>
            )}

            {section === "datos" && (
                <DatosForm form={form} setForm={setForm} canEdit={canEdit} edad={payload.edad} />
            )}
            {section === "familia" && (
                <FamiliaForm form={form} setForm={setForm} canEdit={canEdit} studentId={studentId} onPhotoUploaded={load} />
            )}
            {section === "medico" && (
                <MedicoForm form={form} setForm={setForm} canEdit={canEdit} />
            )}

            {canEdit && (
                <div className="flex justify-end mt-6">
                    <button onClick={save} disabled={saving} className="edu-btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                        <Save size={15} /> {saving ? "Guardando…" : "Guardar"}
                    </button>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">{label}</span>
            {children}
        </div>
    );
}

function DatosForm({ form, setForm, canEdit, edad }) {
    return (
        <div className="edu-card p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <Field label="Nombres">
                    <input disabled={!canEdit} value={form.nombres || ""} onChange={(e) => setForm({ ...form, nombres: e.target.value })} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Apellidos">
                    <input disabled={!canEdit} value={form.apellidos || ""} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} className="edu-input w-full disabled:opacity-60" />
                </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <Field label={`Fecha de nacimiento${edad != null ? ` — ${edad} años` : ""}`}>
                    <input type="date" disabled={!canEdit} value={form.fecha_nacimiento || ""} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Sexo">
                    <select disabled={!canEdit} value={form.sexo || ""} onChange={(e) => setForm({ ...form, sexo: e.target.value })} className="edu-input w-full disabled:opacity-60">
                        <option value="">—</option>
                        {SEXO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </Field>
            </div>
            <Field label="Religión">
                <input disabled={!canEdit} value={form.religion || ""} onChange={(e) => setForm({ ...form, religion: e.target.value })} className="edu-input w-full disabled:opacity-60" />
            </Field>
        </div>
    );
}

function FamiliaForm({ form, setForm, canEdit, studentId, onPhotoUploaded }) {
    const padre = form.padre || {};
    const madre = form.madre || {};
    const hermanos = form.hermanos || [];

    function setPadre(field, value) { setForm({ ...form, padre: { ...padre, [field]: value } }); }
    function setMadre(field, value) { setForm({ ...form, madre: { ...madre, [field]: value } }); }

    async function uploadPhoto(parent, file) {
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/student-details/${studentId}/familia/photo?parent=${parent}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (parent === "padre") setPadre("foto_file_id", data.file_id); else setMadre("foto_file_id", data.file_id);
            toast.success("Foto subida");
            onPhotoUploaded?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo la foto");
        }
    }

    function toggleTieneHermanos(value) {
        if (value && hermanos.length === 0) {
            setForm({ ...form, tiene_hermanos: value, hermanos: [{ nombre: "", edad: null, en_colegio: false }] });
        } else {
            setForm({ ...form, tiene_hermanos: value });
        }
    }

    function setHermano(idx, field, value) {
        const next = hermanos.map((h, i) => (i === idx ? { ...h, [field]: value } : h));
        setForm({ ...form, hermanos: next });
    }

    function addHermano() {
        setForm({ ...form, hermanos: [...hermanos, { nombre: "", edad: null, en_colegio: false }] });
    }

    function removeHermano(idx) {
        setForm({ ...form, hermanos: hermanos.filter((_, i) => i !== idx) });
    }

    return (
        <div className="space-y-4">
            <PersonaCard title="Padre" persona={padre} setField={setPadre} canEdit={canEdit} onUploadPhoto={(f) => uploadPhoto("padre", f)} />
            <PersonaCard title="Madre" persona={madre} setField={setMadre} canEdit={canEdit} onUploadPhoto={(f) => uploadPhoto("madre", f)} />

            <div className="edu-card p-6">
                <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-sm">¿Tiene hermanos?</p>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            disabled={!canEdit}
                            checked={!!form.tiene_hermanos}
                            onChange={(e) => toggleTieneHermanos(e.target.checked)}
                            className="w-4 h-4 disabled:opacity-60"
                        />
                        <span className="text-xs text-edu-muted">{form.tiene_hermanos ? "Activado" : "Desactivado"}</span>
                    </label>
                </div>

                {form.tiene_hermanos && (
                    <div className="space-y-3 mt-4">
                        {hermanos.map((h, idx) => (
                            <div key={idx} className="grid grid-cols-[1fr_90px_110px_auto] gap-3 items-end border-t border-edu pt-3 first:border-t-0 first:pt-0">
                                <Field label="Nombre">
                                    <input disabled={!canEdit} value={h.nombre || ""} onChange={(e) => setHermano(idx, "nombre", e.target.value)} className="edu-input w-full disabled:opacity-60" />
                                </Field>
                                <Field label="Edad">
                                    <input type="number" min="0" disabled={!canEdit} value={h.edad ?? ""} onChange={(e) => setHermano(idx, "edad", e.target.value === "" ? null : parseInt(e.target.value, 10))} className="edu-input w-full disabled:opacity-60" />
                                </Field>
                                <Field label="En el colegio">
                                    <label className="inline-flex items-center gap-2 h-[38px]">
                                        <input type="checkbox" disabled={!canEdit} checked={!!h.en_colegio} onChange={(e) => setHermano(idx, "en_colegio", e.target.checked)} className="w-4 h-4 disabled:opacity-60" />
                                    </label>
                                </Field>
                                {canEdit && (
                                    <button type="button" onClick={() => removeHermano(idx)} className="h-[38px] w-[38px] inline-flex items-center justify-center rounded-lg border border-edu text-edu-muted hover:text-red-600 hover:border-red-300">
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {canEdit && (
                            <button type="button" onClick={addHermano} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-edu hover:bg-edu-soft">
                                <Plus size={12} /> Agregar hermano
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function PersonaCard({ title, persona, setField, canEdit, onUploadPhoto }) {
    return (
        <div className="edu-card p-6">
            <div className="flex items-center gap-4 mb-4">
                {persona.foto_file_id ? (
                    <img src={fileUrl(persona.foto_file_id)} alt={title} className="w-14 h-14 rounded-full object-cover border border-edu" />
                ) : (
                    <div className="w-14 h-14 rounded-full bg-edu-soft flex items-center justify-center border border-edu">
                        <User size={20} className="text-edu-muted" />
                    </div>
                )}
                <div className="flex-1">
                    <p className="font-semibold text-sm">{title}</p>
                    {canEdit && (
                        <label className="mt-1 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-edu hover:bg-edu-soft cursor-pointer">
                            <Upload size={11} /> Subir foto
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onUploadPhoto(e.target.files[0])} />
                        </label>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <Field label="Nombres">
                    <input disabled={!canEdit} value={persona.nombres || ""} onChange={(e) => setField("nombres", e.target.value)} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Apellidos">
                    <input disabled={!canEdit} value={persona.apellidos || ""} onChange={(e) => setField("apellidos", e.target.value)} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Correo">
                    <input disabled={!canEdit} value={persona.correo || ""} onChange={(e) => setField("correo", e.target.value)} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Teléfono">
                    <input disabled={!canEdit} value={persona.telefono || ""} onChange={(e) => setField("telefono", e.target.value)} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Ocupación">
                    <input disabled={!canEdit} value={persona.ocupacion || ""} onChange={(e) => setField("ocupacion", e.target.value)} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Lugar de trabajo">
                    <input disabled={!canEdit} value={persona.lugar_trabajo || ""} onChange={(e) => setField("lugar_trabajo", e.target.value)} className="edu-input w-full disabled:opacity-60" />
                </Field>
            </div>
        </div>
    );
}

function MedicoForm({ form, setForm, canEdit }) {
    return (
        <div className="edu-card p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <Field label="Peso (kg)">
                    <input type="number" step="0.1" disabled={!canEdit} value={form.peso_kg ?? ""} onChange={(e) => setForm({ ...form, peso_kg: e.target.value === "" ? null : parseFloat(e.target.value) })} className="edu-input w-full disabled:opacity-60" />
                </Field>
                <Field label="Altura (cm)">
                    <input type="number" step="0.1" disabled={!canEdit} value={form.altura_cm ?? ""} onChange={(e) => setForm({ ...form, altura_cm: e.target.value === "" ? null : parseFloat(e.target.value) })} className="edu-input w-full disabled:opacity-60" />
                </Field>
            </div>
            <Field label="Tipo de sangre">
                <select disabled={!canEdit} value={form.tipo_sangre || ""} onChange={(e) => setForm({ ...form, tipo_sangre: e.target.value })} className="edu-input w-full disabled:opacity-60">
                    <option value="">—</option>
                    {BLOOD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </Field>
            <Field label="Discapacidad">
                <textarea disabled={!canEdit} value={form.discapacidad || ""} onChange={(e) => setForm({ ...form, discapacidad: e.target.value })} rows={2} className="edu-input w-full disabled:opacity-60" placeholder="Ninguna, o descripción" />
            </Field>
            <Field label="Datos psicológicos">
                <textarea disabled={!canEdit} value={form.datos_psicologicos || ""} onChange={(e) => setForm({ ...form, datos_psicologicos: e.target.value })} rows={3} className="edu-input w-full disabled:opacity-60" />
            </Field>
            <div className="border-t border-edu pt-4">
                <p className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-2">Contacto de emergencia</p>
                <div className="grid grid-cols-3 gap-4">
                    <Field label="Nombre">
                        <input disabled={!canEdit} value={form.contacto_emergencia_nombre || ""} onChange={(e) => setForm({ ...form, contacto_emergencia_nombre: e.target.value })} className="edu-input w-full disabled:opacity-60" />
                    </Field>
                    <Field label="Teléfono">
                        <input disabled={!canEdit} value={form.contacto_emergencia_telefono || ""} onChange={(e) => setForm({ ...form, contacto_emergencia_telefono: e.target.value })} className="edu-input w-full disabled:opacity-60" />
                    </Field>
                    <Field label="Relación">
                        <input disabled={!canEdit} value={form.contacto_emergencia_relacion || ""} onChange={(e) => setForm({ ...form, contacto_emergencia_relacion: e.target.value })} className="edu-input w-full disabled:opacity-60" placeholder="ej. Tía, abuelo…" />
                    </Field>
                </div>
            </div>
        </div>
    );
}
